import { useEffect, useState } from 'react';
import styled from 'styled-components';
import i18n from 'src/utils/translate';
import FeaturedPost from 'src/modules/community/shared/components/featuredPosts/featuredPost';
import { useDispatch, useSelector } from 'src/store/store';
import { fetchFeaturedPosts, hideFloatingSignupBox } from 'src/modules/shared/context/actions';
import { formatFeaturedPostsForSinglePost } from 'src/utils/featuredPosts';
import { Featured } from 'src/constants/types';
import SkipFeaturedPostsButton from 'src/modules/community/singlePost/components/skipFeaturedPostsButton';
import { textSmall } from 'styles-js/mixins/typography';

export default function FeaturedPosts() {
  const featuredPosts = useFetchFeaturedPosts();
  const HUStickyCampaign = useHUStickyCampaign();

  return (
    <>
      {featuredPosts?.length > 0 && (
        <Wrapper>
          <Header>
            <p>{i18n.t('Featured Content')}</p>
            <SkipFeaturedPostsButton />
          </Header>
          <List>
            {featuredPosts.map((featuredPost) =>
              <FeaturedPost featuredPost={featuredPost} key={featuredPost.featuredPostId} />,
            )}
          </List>
        </Wrapper>
      )}
      {HUStickyCampaign && <FeaturedPost featuredPost={HUStickyCampaign} isSticky={true} />}
    </>
  );
}

const useFetchFeaturedPosts = () => {
  const dispatch = useDispatch();
  const { post } = useSelector(({ pages }) => pages.singlePost);
  const { currentUser, currentCommunity, lang } = useSelector(({ context }) => context);

  useEffect(() => {
    if (currentCommunity && post) {
      dispatch(fetchFeaturedPosts({
        communityId: currentCommunity.communityId,
        countryId: 'all',
        language: lang || 'en',
        postId: post.id,
        userType: currentUser?.userId ? 'member' : 'visitor',
      }));
    }
  }, [currentCommunity?.communityId, lang, currentUser?.userId, post?.id]);

  return useFormattedFeaturedPosts();
};

const useFormattedFeaturedPosts = () => {
  const featuredPosts = useSelector(({ context }) => context.currentCommunity?.featuredPosts);
  const dismissedFeaturedPostIds = useSelector(({ context }) => context.currentCommunity?.dismissedFeaturedPostIds);

  const [formattedFeaturedPosts, setFormattedFeaturedPosts] = useState<Featured[]>([]);

  useEffect(() => {
    if (!featuredPosts || featuredPosts.length === 0) return;
    const filteredFeaturedPosts = featuredPosts.filter(({ featuredPostId }: Featured) =>
      !dismissedFeaturedPostIds?.includes(featuredPostId));
    setFormattedFeaturedPosts(formatFeaturedPostsForSinglePost(filteredFeaturedPosts));
  }, [featuredPosts, dismissedFeaturedPostIds]);

  return formattedFeaturedPosts;
};

const useHUStickyCampaign = () => {
  const dispatch = useDispatch();
  const featuredPosts = useSelector(({ context }) => context.currentCommunity?.featuredPosts);
  const dismissedFeaturedPostIds = useSelector(({ context }) => context.currentCommunity?.dismissedFeaturedPostIds);
  const { currentUser, isFloatingSignupBoxVisible } = useSelector(({ context }) => context);
  const [firstHUCampaign, setFirstHUCampaign] = useState<Featured | undefined>(undefined);

  useEffect(() => {
    if (!featuredPosts || featuredPosts.length === 0) return;
    setFirstHUCampaign(featuredPosts?.find(({ featuredByCommunityName, featuredPostId }) =>
      featuredByCommunityName === 'HealthUnlocked' && !dismissedFeaturedPostIds?.includes(featuredPostId)));
  }, [featuredPosts, dismissedFeaturedPostIds]);

  useEffect(() => {
    // If we have an HU sticky campaign to show, we hide FloatingSignupBox for visitors on the single post page
    if (firstHUCampaign && isFloatingSignupBoxVisible) {
      dispatch(hideFloatingSignupBox());
    }
  }, [firstHUCampaign, isFloatingSignupBoxVisible, dispatch]);

  const showCampaign = !currentUser && firstHUCampaign;

  return showCampaign ? firstHUCampaign : null;
};

const Wrapper = styled.div`
  margin: 30px 0;
`;
const Header = styled.div`
  ${textSmall}
  border-top: 0;
  padding-bottom: 10px;
  color: ${({ theme }) => theme.colorTextLight};
  display: flex;
  justify-content: space-between;

  @media (${({ theme }) => theme.underScreenSmall}) {
    border-bottom: 1px solid ${({ theme }) => theme.colorGreyLight};
  }
`;
const List = styled.div`
  @media (${({ theme }) => theme.overScreenSmall}) {
    border: 1px solid ${({ theme }) => theme.colorGreyLight};
  }
  .results-post {
    @media (${({ theme }) => theme.underScreenSmall}) {
      padding: 20px 0;
    }
  }
`;
